import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { User, SessionStatus } from '../types';
import apiService from '../services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionStatus: SessionStatus | null;
  mfaRequired: boolean;
  mfaVerified: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_SESSION_STATUS'; payload: SessionStatus }
  | { type: 'SET_MFA_STATUS'; payload: { required: boolean; verified: boolean } }
  | { type: 'LOGOUT' }
  | { type: 'LOGIN_SUCCESS'; payload: User };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  sessionStatus: null,
  mfaRequired: false,
  mfaVerified: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: !!action.payload,
        isLoading: false 
      };
    case 'SET_SESSION_STATUS':
      return { 
        ...state, 
        sessionStatus: action.payload,
        mfaRequired: action.payload.mfa_required || false,
        mfaVerified: action.payload.mfa_verified || false,
        isAuthenticated: action.payload.authenticated,
        isLoading: action.payload.authenticated ? true : false
      };
    case 'SET_MFA_STATUS':
      return { 
        ...state, 
        mfaRequired: action.payload.required,
        mfaVerified: action.payload.verified
      };
    case 'LOGIN_SUCCESS':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: true, 
        isLoading: false,
        mfaRequired: false,
        mfaVerified: false
      };
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        sessionStatus: null,
        mfaRequired: false,
        mfaVerified: false
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (credentials: { username: string; password: string; remember_me?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  verifyMFA: (token: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  forceSessionCheck: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [isCheckingSession, setIsCheckingSession] = React.useState(false);

  useEffect(() => {
    const initializeCSRF = async () => {
      try {
        const existingToken = document.cookie.includes('csrftoken=');
        if (!existingToken) {
          await apiService.getCSRFTokenFromServer();
        }
      } catch (error) {
        console.warn('Failed to initialize CSRF token:', error);
      }
    };
    
    initializeCSRF();
  }, []);
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      console.log('Session expired, redirecting to login...');
      dispatch({ type: 'LOGOUT' });
      const redirectUrl = event.detail?.redirectUrl || '/auth/signin';
      if (window.location.pathname !== redirectUrl && !window.location.pathname.includes('/auth/')) {
        window.location.href = redirectUrl;
      }
    };

    window.addEventListener('session-expired', handleSessionExpired as EventListener);
    
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired as EventListener);
    };
  }, []);

  const checkSession = useCallback(async () => {
    if (isCheckingSession) {
      return;
    }
    
    try {
      setIsCheckingSession(true);
      dispatch({ type: 'SET_LOADING', payload: true });
      const sessionStatus = await apiService.getSessionStatus();
      dispatch({ type: 'SET_SESSION_STATUS', payload: sessionStatus });
      
      if (sessionStatus.authenticated && !sessionStatus.mfa_required) {
        const user = await apiService.getCurrentUser();
        dispatch({ type: 'SET_USER', payload: user });
      } else {
        // User is not authenticated, set loading to false
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Session check failed:', error);
      // Set loading to false and logout on error
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'LOGOUT' });
    } finally {
      setIsCheckingSession(false);
    }
  }, [isCheckingSession]);

  const login = async (credentials: { username: string; password: string; remember_me?: boolean }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await apiService.login(credentials);
      
      if (response.status === 'success') {
        const user = await apiService.getCurrentUser();
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      } else if (response.status === 'mfa_required') {
        dispatch({ type: 'SET_MFA_STATUS', payload: { required: true, verified: false } });
        dispatch({ type: 'SET_LOADING', payload: false });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const verifyMFA = async (token: string) => {
    try {
      const response = await apiService.verifyMFA(token);
      if (response.status === 'success') {
        const user = await apiService.getCurrentUser();
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
        dispatch({ type: 'SET_MFA_STATUS', payload: { required: false, verified: true } });
      } else {
        throw new Error(response.message || 'MFA verification failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshUser = async () => {
    try {
      const user = await apiService.getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      console.error('Failed to refresh user:', error);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const forceSessionCheck = async () => {
    await checkSession();
  };

  useEffect(() => {
    // Only check session once on mount
    checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic session check (every 5 minutes) to detect session expiry
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const intervalId = setInterval(async () => {
      try {
        const sessionStatus = await apiService.getSessionStatus();
        if (!sessionStatus.authenticated) {
          console.log('Session expired during periodic check');
          dispatch({ type: 'LOGOUT' });
          window.location.href = '/auth/signin';
        }
      } catch (error) {
        console.error('Session check failed:', error);
        // On error, assume session expired
        dispatch({ type: 'LOGOUT' });
        window.location.href = '/auth/signin';
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(intervalId);
  }, [state.isAuthenticated]);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkSession,
    verifyMFA,
    refreshUser,
    forceSessionCheck,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
